import React, { useEffect, useState, Fragment } from 'react'
import { Button, DeemphasizedLink, Modal, QuantityInput, Text } from '@fielded/shared-ui'
import { SHIPMENT_ADJUSTMENT_REASONS } from '@fielded/fs-api/lib/shipment/constants'

const CountReasonsModal = ({
  isOpen,
  onClose,
  onClickProceed,
  picked,
  currentQty,
  productName
}) => {
  const [reasonsQts, setReasonsQts] = useState({})
  const [qtyLeft, setQtyLeft] = useState(0)

  // adjustment = deduction only
  const adjustment = picked > currentQty ? picked - currentQty : 0
  const isInvalidIncrease = currentQty > picked

  useEffect(() => {
    if (isOpen) {
      setReasonsQts({})
      setQtyLeft(adjustment)
    }
  }, [isOpen, adjustment])

  const updateQty = (reasonKey, qty) => {
    const safeQty = Number(qty) || 0
    const newReasonQts = { ...reasonsQts, [reasonKey]: safeQty }

    const totalSpent = Object.values(newReasonQts)
      .reduce((acc, current) => acc + Number(current || 0), 0)

    const totalLeft = adjustment - totalSpent

    setQtyLeft(totalLeft)
    setReasonsQts(newReasonQts)
  }

  const canProceed = qtyLeft === 0 && adjustment > 0 && !isInvalidIncrease

  const handleProceed = () => {
  // Recalculate totals as extra safety ie manipulation via devtools
    const totalSpent = Object.values(reasonsQts)
      .reduce((acc, current) => acc + Number(current || 0), 0)

    const totalLeft = adjustment - totalSpent

    // Block submission if math doesn’t tally or invalid increase
    if (isInvalidIncrease || totalLeft !== 0 || adjustment <= 0) {
      console.warn('Invalid submission: quantities do not tally.')
      return
    }

    const filteredReasons = Object.fromEntries(
      Object.entries(reasonsQts).filter(([_, qty]) => qty > 0)
    )
    onClickProceed(filteredReasons)
  }

  return (
    <Modal
      centered={false}
      isOpen={isOpen}
      title={`Adjusting ${productName}`}
      onClose={onClose}
    >
      <section>
        {isInvalidIncrease && (
          <Text bold color='error' className='vs-u-margin-bottom'>
            You entered a quantity higher than expected ({currentQty} vs {picked}).
            Adjustments can only reduce the picked amount.
            Please go back and enter a lower number.
          </Text>
        )}

        {!isInvalidIncrease && adjustment > 0 && (
          <Fragment>
            <Text bold className='vs-u-margin-bottom'>
              Please enter the quantity for each reason you're adjusting the product
            </Text>
            <div className='count-reason-modal__info-box vs-u-margin-bottom'>
              Adjusted quantity remaining: {qtyLeft}
            </div>
            <div>
              {Object.entries(SHIPMENT_ADJUSTMENT_REASONS).map(([key, description]) => (
                <div
                  className='vs-u-row-spaced vs-u-align-center vs-u-margin-bottom'
                  key={key}
                >
                  <span>{description}</span>
                  <QuantityInput
                    className='count-reason-modal__qty-input'
                    textAlign='right'
                    value={reasonsQts[key] || 0}
                    onValueChange={({ value }) => updateQty(key, value)}
                    allowNegative={false}
                    blockDecimal
                  />
                </div>
              ))}
            </div>
          </Fragment>
        )}

        {!isInvalidIncrease && adjustment === 0 && (
          <Text bold>No adjustment is required (quantities match).</Text>
        )}

        <section className='count-reason-modal__buttons'>
          <DeemphasizedLink
            colorVariant='brand'
            onClick={onClose}
            bold
          >
            Cancel
          </DeemphasizedLink>

          <Button
            colorVariant='warning'
            fill='full'
            disabled={!canProceed}
            onClick={handleProceed}
          >
            Save & close
          </Button>
        </section>
      </section>
    </Modal>
  )
}

export default CountReasonsModal
